import { Controller } from "@hotwired/stimulus"

// Karat purity factors (fraction of pure gold)
const KARAT_PURITY = {
  "24K": 24 / 24,
  "22K": 22 / 24,
  "18K": 18 / 24,
  "14K": 14 / 24,
  "10K": 10 / 24
}

const TROY_OZ_TO_GRAM = 31.1035

export default class extends Controller {
  static targets = [
    "goldOzPrice", "silverOzPrice", "platinumOzPrice",
    "goldGramPrice", "silverGramPrice", "platinumGramPrice",
    "weightInput", "weightUnit",
    "goldResults", "silverResult", "platinumResult",
    "updatedAt", "statusBadge"
  ]

  connect() {
    this.fetchPrices()
  }

  // Fetch live prices from backend
  async fetchPrices() {
    try {
      this.updateStatus("loading")
      const response = await fetch("/precious-metal-calculator/prices.json")
      const data = await response.json()

      if (data.prices) {
        this.applyPrices(data.prices, data.fallback)
      }
    } catch (error) {
      console.warn("Failed to fetch metal prices:", error)
      this.updateStatus("offline")
    }
  }

  applyPrices(prices, isFallback) {
    if (prices.XAU) {
      this.goldOzPriceTarget.value = parseFloat(prices.XAU.price_oz).toFixed(2)
      this.goldGramPriceTarget.textContent = `$${parseFloat(prices.XAU.price_gram).toFixed(2)}`
    }
    if (prices.XAG) {
      this.silverOzPriceTarget.value = parseFloat(prices.XAG.price_oz).toFixed(2)
      this.silverGramPriceTarget.textContent = `$${parseFloat(prices.XAG.price_gram).toFixed(2)}`
    }
    if (prices.XPT) {
      this.platinumOzPriceTarget.value = parseFloat(prices.XPT.price_oz).toFixed(2)
      this.platinumGramPriceTarget.textContent = `$${parseFloat(prices.XPT.price_gram).toFixed(2)}`
    }

    this.updateStatus(isFallback ? "fallback" : "live")
    this.calculate()
  }

  // Called when user changes any market price input
  marketPriceChanged() {
    this.updateGramDisplays()
    this.calculate()
  }

  // Update the gram price displays from oz inputs
  updateGramDisplays() {
    const goldOz = parseFloat(this.goldOzPriceTarget.value) || 0
    const silverOz = parseFloat(this.silverOzPriceTarget.value) || 0
    const platinumOz = parseFloat(this.platinumOzPriceTarget.value) || 0

    this.goldGramPriceTarget.textContent = `$${(goldOz / TROY_OZ_TO_GRAM).toFixed(2)}`
    this.silverGramPriceTarget.textContent = `$${(silverOz / TROY_OZ_TO_GRAM).toFixed(2)}`
    this.platinumGramPriceTarget.textContent = `$${(platinumOz / TROY_OZ_TO_GRAM).toFixed(2)}`
  }

  // Main calculation
  calculate(event) {
    this.flashResults = !!event && [this.weightInputTarget, this.weightUnitTarget].includes(event.target)

    const goldOz = parseFloat(this.goldOzPriceTarget.value) || 0
    const silverOz = parseFloat(this.silverOzPriceTarget.value) || 0
    const platinumOz = parseFloat(this.platinumOzPriceTarget.value) || 0

    const weight = parseFloat(this.weightInputTarget.value) || 0
    const unit = this.weightUnitTarget.value

    // Convert weight to grams
    let weightInGrams = weight
    if (unit === "oz") {
      weightInGrams = weight * TROY_OZ_TO_GRAM
    } else if (unit === "kg") {
      weightInGrams = weight * 1000
    } else if (unit === "dwt") {
      weightInGrams = weight * 1.55517
    }

    // Gold karat calculations
    const goldPerGram = goldOz / TROY_OZ_TO_GRAM
    const goldResultsHtml = Object.entries(KARAT_PURITY).map(([karat, purity]) => {
      const pricePerGram = goldPerGram * purity
      const totalPrice = pricePerGram * weightInGrams
      return this.resultRow(karat, `${(purity * 100).toFixed(1)}%`, pricePerGram, totalPrice, weightInGrams)
    }).join("")
    this.goldResultsTarget.innerHTML = goldResultsHtml

    // Silver calculation
    const silverPerGram = silverOz / TROY_OZ_TO_GRAM
    const silverTotal = silverPerGram * weightInGrams
    this.silverResultTarget.innerHTML =
      this.resultRow("Pure .999", "99.9%", silverPerGram, silverTotal, weightInGrams) +
      this.resultRow("Sterling .925", "92.5%", silverPerGram * 0.925, silverTotal * 0.925, weightInGrams)

    // Platinum calculation
    const platinumPerGram = platinumOz / TROY_OZ_TO_GRAM
    const platinumTotal = platinumPerGram * weightInGrams
    this.platinumResultTarget.innerHTML =
      this.resultRow("Pt950", "95.0%", platinumPerGram * 0.95, platinumTotal * 0.95, weightInGrams) +
      this.resultRow("Pt900", "90.0%", platinumPerGram * 0.90, platinumTotal * 0.90, weightInGrams)
  }

  // Render a single result row (card-style, no table)
  resultRow(label, purity, pricePerGram, total, weightInGrams) {
    const hasWeight = weightInGrams > 0
    const flashClass = this.flashResults ? " pmc-flash" : ""
    const priceDisplay = hasWeight
      ? `<span class="pmc-total small${flashClass}">$${total.toFixed(2)}</span>`
      : `<span class="small text-muted${flashClass}">$${pricePerGram.toFixed(2)}/g</span>`
    return `
      <div class="pmc-result-row d-flex align-items-center justify-content-between py-2">
        <div>
          <span class="fw-medium small">${label}</span>
          <span class="text-muted" style="font-size: 0.7rem; margin-left: 4px;">${purity}</span>
        </div>
        <div class="text-end">
          ${priceDisplay}
        </div>
      </div>
    `
  }

  // Refresh prices from API
  refresh() {
    this.fetchPrices()
  }

  updateStatus(status) {
    if (!this.hasStatusBadgeTarget) return

    const badge = this.statusBadgeTarget
    badge.classList.remove("bg-success", "bg-warning", "bg-secondary", "bg-info")

    switch (status) {
      case "live":
        badge.classList.add("bg-success")
        badge.textContent = "Live Prices"
        break
      case "fallback":
        badge.classList.add("bg-warning")
        badge.textContent = "Estimated Prices"
        break
      case "loading":
        badge.classList.add("bg-info")
        badge.textContent = "Loading..."
        break
      case "offline":
        badge.classList.add("bg-secondary")
        badge.textContent = "Offline — Enter Manually"
        break
    }

    if (this.hasUpdatedAtTarget) {
      const now = new Date()
      this.updatedAtTarget.textContent = now.toLocaleString()
    }
  }
}
